import ProfessionalDetailRootPage from '~/modules/professional/page/ProfessionalPage';
import { loader } from '~/modules/professional/page/loaders/professional-page-loader.server';
import { shouldRevalidateProfessionalLayout } from '~/modules/professional/util/professional-layout-revalidation';

import {
    createOGTags,
    fallbackOGSocialImage,
    getProfessionalBannerByPriority,
    getCanonicalHreflang,
    mergeMeta,
} from '@modules/seo';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { getProfessionalPageTitle } from '~/modules/seo/util/get-professional-page-title';
import { getProfessionalPageMetaDescription } from '~/modules/seo/util/get-professional-page-meta-description';
import { createProfessionalSchema } from '~/modules/seo/schema/professional-schema';
import { createFAQSchema } from '~/modules/seo/schema/faq-page-schema';
import { noIndexCrossCountry } from '~/modules/seo/util/no-index-cross-country';
import { getRouteData } from '~/modules/root/util/get-route-data';
import type { APHandle } from '~/modules/root';

export const meta = mergeMeta<typeof loader>(
    (_props) => {
        return [];
    },
    (props) => {
        const { data, matches } = props;
        if (!data?.initial) {
            return [];
        }
        const { rootData } = getRouteData(matches);

        const title = getProfessionalPageTitle(props, data.initial?.Title);
        const description = getProfessionalPageMetaDescription(
            props,
            [
                data?.localisation?.Description,
                data?.localisation?.DirectoryHeader?.Content,
            ],
            data.initial?.Title,
        );

        const fallbackSocialImage = fallbackOGSocialImage(
            props,
            getProfessionalBannerByPriority(props),
        );

        const ldSchemaElements = [
            createFAQSchema(data.localisation?.FAQSchemas ?? []),
        ];

        if (data.shouldIncludeLocalBusinessSchema) {
            const professionalSchema = createProfessionalSchema(
                props as Parameters<typeof createProfessionalSchema>[0],
            );
            if (professionalSchema) {
                ldSchemaElements.push(professionalSchema);
            }
        }

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
                facebookSocialImage:
                    data?.initial?.FacebookPreviewImage ?? fallbackSocialImage,
                type: 'website',
            }),
            ...getCanonicalHreflang(props, {
                professionalCountry: data.initial.Country,
                noAlternateLinks: true,
            }),
            ...createLDSchema(ldSchemaElements),
            ...noIndexCrossCountry(
                data?.initial?.Country,
                rootData?.localisation?.country,
                data?.localisation,
            ),
        ];
    },
);

export const handle: APHandle = {
    fadeTopNavAfterPosition: 200,
    isProfessionalProfile: true,
    hideContactArchiPro: true,
    // Homepage-style hero (BC-4481): big "A" logomark over the banner.
    // `ground` is the profile's own: once the banner has scrolled away the bar
    // goes solid, and the default white band cut across a page that is grey.
    header: {
        showLogomark: true,
        headerVariant: 'transparent',
        ground: 'bg-[#f5f5f7]',
    },
};

export { loader };
export const shouldRevalidate = shouldRevalidateProfessionalLayout;
export default ProfessionalDetailRootPage;
